"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Token = exports.SharedKey = exports.PublicKey = exports.PrivateKey = void 0;
const utils_1 = require("./utils");
const webcrypto = typeof crypto == 'undefined' ? global.crypto : crypto;
class PrivateKey {
    constructor(use, importedKey, name) {
        this.exportedKey = importedKey;
        this.name = importedKey ? importedKey.split('-----END PRIVATE KEY-----').slice(1).join('-----END PRIVATE KEY-----').replace('\n', '') : name;
        if (use === 'sign') {
            this._algorithm = 'ECDSA';
            this._usage = ['sign'];
            return;
        }
        this._algorithm = 'ECDH';
        this._usage = ['deriveKey'];
        // this.publicSerial(); // <- don't do this!
        return;
    }
    generate() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.exportedKey !== undefined) {
                let privateKey, jwk;
                privateKey = yield webcrypto.subtle.importKey("pkcs8", (0, utils_1.b64decode)(this.exportedKey.split('-----END PRIVATE KEY-----')[0].split('\n').slice(1).join('')), {
                    name: this._algorithm,
                    namedCurve: "P-256"
                }, true, this._usage);
                jwk = yield webcrypto.subtle.exportKey("jwk", privateKey);
                this.key = {
                    privateKey: privateKey,
                    publicKey: yield webcrypto.subtle.importKey('jwk', { alg: jwk.alg, crv: jwk.crv, ext: true, key_ops: [], kty: jwk.kty, x: jwk.x, y: jwk.y }, {
                        name: this._algorithm,
                        namedCurve: "P-256"
                    }, true, [])
                };
            }
            else {
                this.key = yield webcrypto.subtle.generateKey({
                    name: this._algorithm,
                    namedCurve: "P-256"
                }, true, this._usage);
            }
            return this;
        });
    }
    sign(message) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.key === undefined) {
                yield this.generate();
            }
            if (this.key !== undefined) {
                let signatureBuf = yield webcrypto.subtle.sign({
                    name: "ECDSA",
                    hash: { name: "SHA-256" },
                }, this.key.privateKey, message);
                return new Uint8Array(signatureBuf);
            }
            throw new Error('key is undefined');
        });
    }
    publicSerial(showName = true) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.key === undefined) {
                yield this.generate();
            }
            if (this.key !== undefined) {
                let publicKey = (yield webcrypto.subtle.exportKey('raw', this.key.publicKey)).slice(1);
                let out = (this.name && showName ? `${this.name}.` : '') + (0, utils_1.b64encode)(new Uint8Array(publicKey));
                if (this.repr === undefined) {
                    this.repr = out;
                }
                return out;
            }
            throw new Error('key is undefined');
        });
    }
    exportKey() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.exportedKey == undefined) {
                if (this.key === undefined) {
                    yield this.generate();
                }
                if (this.key !== undefined) {
                    this.exportedKey =
                        "-----BEGIN PRIVATE KEY-----\n" +
                            (0, utils_1.b64encode)(new Uint8Array(yield webcrypto.subtle.exportKey("pkcs8", this.key.privateKey))) +
                            "\n-----END PRIVATE KEY-----" + (this.name ? `\n${this.name}` : '');
                }
                else {
                    throw new Error('key is undefined');
                }
            }
            return this.exportedKey;
        });
    }
}
exports.PrivateKey = PrivateKey;
class PublicKey {
    constructor(use, publicSerial) {
        this.publicSerial = publicSerial;
        this.name = publicSerial.split('.').slice(0, -1).join('.');
        if (use === 'verify') {
            this._algorithm = 'ECDSA';
            this._usage = ['verify'];
            return;
        }
        if (use === 'derive') {
            this._algorithm = 'ECDH';
            this._usage = [];
            return;
        }
        throw new Error("expected input 'use' should be 'verify' or 'derive'");
    }
    generate() {
        return __awaiter(this, void 0, void 0, function* () {
            let decodedPublicSerial = (0, utils_1.b64decode)(this.publicSerial.split('\n').slice(-1)[0]);
            this.key = yield webcrypto.subtle.importKey('raw', new Uint8Array([4, ...decodedPublicSerial]).buffer, {
                name: this._algorithm,
                namedCurve: 'P-256'
            }, true, this._usage);
            return this;
        });
    }
    verify(signature, message) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.key === undefined) {
                yield this.generate();
            }
            if (this.key !== undefined) {
                if (yield webcrypto.subtle.verify({
                    name: "ECDSA",
                    hash: { name: "SHA-256" },
                }, this.key, signature, message)) {
                    return;
                }
                else {
                    throw new Error('Invalid Signature');
                }
            }
            throw new Error('key is undefined');
        });
    }
}
exports.PublicKey = PublicKey;
class SharedKey {
    constructor(privateKey, publicSerial) {
        this.publicSerial = publicSerial;
        this.privateKey = privateKey;
    }
    generate() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.privateKey.key === undefined) {
                yield this.privateKey.generate();
            }
            if (this.privateKey.key !== undefined) {
                this.key = yield webcrypto.subtle.deriveKey({
                    name: "ECDH",
                    public: (yield new PublicKey('derive', this.publicSerial).generate()).key
                }, this.privateKey.key.privateKey, {
                    name: "AES-CTR",
                    length: 256
                }, false, ['encrypt', 'decrypt']);
                return this;
            }
        });
    }
    encrypt(message, nonce) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.key === undefined) {
                yield this.generate();
            }
            if (this.key !== undefined) {
                let out = yield webcrypto.subtle.encrypt({
                    name: "AES-CTR",
                    counter: nonce,
                    length: 128
                }, this.key, message);
                return new Uint8Array(out);
            }
        });
    }
    decrypt(ciphertext, nonce) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.key === undefined) {
                yield this.generate();
            }
            if (this.key !== undefined) {
                return yield webcrypto.subtle.encrypt({
                    name: "AES-CTR",
                    counter: nonce,
                    length: 128
                }, this.key, ciphertext);
            }
        });
    }
}
exports.SharedKey = SharedKey;
class Token {
    constructor(issuer, brokers, receiver, asset, tokenType, maxDepth, validFrom, validUntil, metadata = {}) {
        this.issuer = issuer;
        this.brokers = brokers;
        this.receiver = receiver;
        this.asset = asset;
        this.tokenType = tokenType;
        this.maxDepth = maxDepth || null;
        this.validFrom = validFrom || Date.now() / 1000;
        this.validUntil = validUntil || null;
        this.metadata = metadata;
    }
    verify(signature) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                yield new PublicKey('verify', this.issuer).verify((0, utils_1.b64decode)(signature), new TextEncoder().encode(this.toString()));
                this.signature = signature;
                return true;
            }
            catch (_a) {
                return false;
            }
        });
    }
    toString() {
        return JSON.stringify(this.toObj());
    }
    toObj() {
        return {
            issuer: this.issuer,
            brokers: this.brokers,
            receiver: this.receiver,
            asset: this.asset,
            token_type: this.tokenType,
            max_depth: this.maxDepth,
            valid_from: this.validFrom,
            valid_until: this.validUntil,
            metadata: this.metadata,
        };
    }
    encode() {
        if (this.signature === undefined) {
            throw 'You need to sign the token before encoding it';
        }
        return this.signature + '.' + this.toString();
    }
    sign(signingKey) {
        return __awaiter(this, void 0, void 0, function* () {
            this.signature = (0, utils_1.b64encode)(yield signingKey.sign((new TextEncoder().encode(this.toString()))));
            return this.signature;
        });
    }
    static decode(encodedToken, verify = true) {
        return __awaiter(this, void 0, void 0, function* () {
            let obj = JSON.parse(encodedToken.substr(encodedToken.indexOf('.') + 1));
            let token = new Token(obj.issuer, obj.brokers, obj.receiver, obj.asset, obj.token_type, obj.max_depth, obj.valid_from, obj.valid_until, obj.metadata);
            let signature = encodedToken.substring(0, encodedToken.indexOf('.'));
            if (!verify) {
                token.signature = signature;
                return token;
            }
            if (yield token.verify(signature)) {
                return token;
            }
            console.error('Invalid Signature');
        });
    }
}
exports.Token = Token;
//# sourceMappingURL=cryptography.js.map